/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Alert, Button, Form } from 'reactstrap';
import { Field, Formik, FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import { Coupon } from '../../../pages/deprecated/payment/types';
import styles from './promo-code.module.scss';
import warning from 'src/assets/icons/warning.svg';
import PromoInput from './promo-code-input';
import paymentAPI from '../../../pages/deprecated/payment/api';
import { IAppState } from '../../../store';
import { validateCoupons } from 'src/util/coupon';
import GenerateCouponText from './promo-code-text';
import {
  trackPromoCodeApplied,
  trackPromoCodeSuccess,
  trackPromoCodeFailed,
} from 'src/util/track';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setCouponObj } from 'src/store/signup';
import { projectCouponCode } from 'src/pages/constants';

type PromoCodeProps = {
  priceId: string;
  coupon: Coupon | undefined;
  setCoupon: (coupon: Coupon | undefined) => void;
  finalPrice: number;
  productId?: string;
  futureCommencement?: boolean;
  setFutureCommencement?: (futureCommencement: boolean) => void;
  prefillCode?: string;
  isFromSignUp?: boolean;
  readOnly?: boolean;
  isChallengeFlowWithUrlCode?: boolean;
};

interface FormInputs {
  couponCode: string;
}

const PromoCode: FC<PromoCodeProps> = ({
  priceId,
  productId = '',
  coupon,
  setCoupon,
  futureCommencement,
  setFutureCommencement = () => {},
  prefillCode,
  isFromSignUp,
  readOnly = false,
  isChallengeFlowWithUrlCode = false,
  finalPrice,
}: PromoCodeProps) => {
  const user = useSelector((state: IAppState) => state.auth?.user);
  const { selectedPlanId, isFitherProjectUser } = useAppSelector(
    state => state.signUp
  );
  const { isPaymentProcessing } = useAppSelector(state => state.payment);
  const formikRef = useRef<FormikProps<FormInputs> | null>(null);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const hasSubmittedChallengeCoupon = useRef(false);

  useEffect(() => {
    if (coupon) {
      resetCoupon();
    }
  }, [selectedPlanId]);

  useEffect(() => {
    if (coupon === undefined && !isFitherProjectUser) {
      dispatch(setCouponObj({}));
    }
  }, [coupon, isFitherProjectUser]);

  useEffect(() => {
    const submitCoupon = async () => {
      if (isFitherProjectUser && user) {
        await submitForm({ couponCode: projectCouponCode }, null);
      }
    };
    setTimeout(() => submitCoupon(), 500); //wait till first initial payment-intent get completed
  }, [isFitherProjectUser, user]);

  // Reset challenge coupon flag when plan changes (priceId changes)
  useEffect(() => {
    if (isChallengeFlowWithUrlCode && prefillCode) {
      hasSubmittedChallengeCoupon.current = false;
    }
  }, [priceId, isChallengeFlowWithUrlCode, prefillCode]);

  useEffect(() => {
    // Submit challenge coupon when conditions are met and no coupon exists yet
    // This will re-trigger when plan changes because hasSubmittedChallengeCoupon is reset above
    if (
      isChallengeFlowWithUrlCode &&
      user &&
      prefillCode &&
      !hasSubmittedChallengeCoupon.current &&
      !coupon
    ) {
      hasSubmittedChallengeCoupon.current = true;
      setTimeout(async () => {
        formikRef.current?.setFieldValue('couponCode', prefillCode);
        await submitForm({ couponCode: prefillCode }, null);
      }, 1000); //wait till all other APIs settle
    }
  }, [isChallengeFlowWithUrlCode, user, prefillCode, priceId, coupon]); // Added priceId to re-trigger when plan changes

  const resetCoupon = () => {
    setCoupon(undefined);
    dispatch(setCouponObj({}));
    formikRef.current?.setFieldValue('couponCode', prefillCode || '');
  };

  const submitForm = async (values: FormInputs, actions: any) => {
    setLoading(true);
    setErrorMessage('');
    if (!user) {
      return;
    }

    const couponCode = values.couponCode.toLowerCase().trim();
    trackPromoCodeApplied(couponCode);

    try {
      const response = await paymentAPI.get('stripe/coupons', {
        params: {
          id: couponCode,
          email: user.email,
        },
      });

      const error = validateCoupons(
        priceId,
        response?.data?.metadata,
        productId,
        response?.data?.applies_to?.products,
        response.data.restrictions,
        finalPrice
      );
      if (error) {
        trackPromoCodeFailed(couponCode, error);
        setErrorMessage(error);
        actions?.setSubmitting(false);
        return;
      }

      if (!response?.data?.id) {
        const errorMsg = response.data.message || "Couldn't find that coupon";
        trackPromoCodeFailed(couponCode, errorMsg);
        setErrorMessage(errorMsg);
        actions?.setSubmitting(false);
        return;
      }

      const couponData = {
        ...response.data,
        id: response.data.id,
      };
      setCoupon(couponData);
      dispatch(setCouponObj(couponData));

      // set event for apply button
      trackPromoCodeSuccess(
        couponCode,
        response.data.amountOff ? response.data.amountOff / 100 : undefined,
        response.data.percentOff || undefined
      );
      if (futureCommencement) {
        setFutureCommencement(true);
      }
    } catch (e) {
      const error =
        (e as any)?.response?.data?.message || "Couldn't find that coupon";
      trackPromoCodeFailed(couponCode, error);
      setErrorMessage(error);
    } finally {
      setLoading(false);
    }

    actions?.setSubmitting(false);
  };

  return (
    <div className={styles.promoCode}>
      <Formik
        innerRef={formikRef}
        onSubmit={submitForm}
        initialValues={{ couponCode: prefillCode || '' }}>
        {(formikProps): JSX.Element => (
          <>
            <Form
              noValidate
              onSubmit={formikProps.handleSubmit}
              onReset={formikProps.handleReset}
              style={{
                background: isFromSignUp ? 'white' : 'transparent',
                borderBottom: !isFromSignUp ? '2px solid #1E202E' : 'none',
                padding: isFromSignUp ? '0px 5%' : '0px',
                borderRadius: isFromSignUp ? '5px' : '0px',
                width: isFromSignUp ? '90%' : '100%',
              }}>
              <Field
                name="couponCode"
                placeholder="Enter promo code"
                component={PromoInput}
                className={styles.input}
                readOnly={readOnly}
                onKeyUp={(): void => {
                  setErrorMessage('');
                  setCoupon(undefined);
                  dispatch(setCouponObj({}));
                }}
              />

              {!isFitherProjectUser && (
                <Button
                  type="submit"
                  disabled={
                    formikProps.isSubmitting ||
                    !user ||
                    !formikProps.values.couponCode ||
                    isPaymentProcessing
                  }
                  className={styles.button}>
                  Apply
                </Button>
              )}
            </Form>
            {!!coupon &&
              errorMessage === '' &&
              !loading &&
              (!coupon.name.toLowerCase().includes('free week') ? (
                <Box color="success">
                  <GenerateCouponText
                    coupon={coupon}
                    couponCode={formikProps.values.couponCode}
                  />
                </Box>
              ) : (
                <Alert color="success">
                  <div>
                    Promo code FREE WEEK applied. FREE WEEK applied to package,
                    first payment in 7 days.
                  </div>
                </Alert>
              ))}
            {errorMessage && (
              <Box className={styles.warningContainer}>
                <img
                  src={warning}
                  alt="warning"
                  className={styles.warningIcon}
                />
                <span className={styles.errorText}>{errorMessage}</span>
              </Box>
            )}
          </>
        )}
      </Formik>
    </div>
  );
};

export default PromoCode;
